import { faTrashAlt } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { SyntheticEvent, useEffect, useRef, useState } from "react";
import { Button, Col, Collapse, Input, Row } from "reactstrap";
import { FileInfo } from "../../models/routes/fileInfo";
import attachmentsService from "../../services/attachmentsService";
import androidService from "../../services/mobile/androidService";
import { pluralFile } from "../../lib/heplers/plural";
import { ConfirmDialog } from "../dialogs/confirmDialog";
import { getVideoPoster } from "./videoGallery/helpers";
import "./videoGallery/videoGalleryStyles.css";

const SHOW_SIZE = 3;

type VideoGalleryItem = {
    info: FileInfo;
    src: string;
    poster: string;
    isSelected: boolean;
};

type Props = {
    videos: FileInfo[];
    disabled?: boolean;
    onRemoveFiles?: (files: FileInfo[]) => Promise<void>;
};

const VideoGallery = ({ videos, disabled, onRemoveFiles }: Props) => {
    const [items, setItems] = useState<VideoGalleryItem[]>([]);
    const [showAllItems, setShowAllItems] = useState(false);
    const [isOpenConfirmDialog, setIsOpenConfirmDialog] = useState(false);
    const [isRemoving, setIsRemoving] = useState(false);

    const hasLastFiles = videos.length > SHOW_SIZE;
    const selectedFiles = items.filter((i) => i.isSelected);
    const hasSelectedFiles = selectedFiles.length > 0;
    const enableRemoveFiles = Boolean(onRemoveFiles);

    const itemsRef = useRef(items);
    useEffect(() => {
        itemsRef.current = items;
    });

    useEffect(() => {
        (async () => {
            setItems((currentItems) =>
                currentItems.filter((item) => {
                    const exists = videos.some((video) => item.info.guid === video.guid);
                    if (!exists) URL.revokeObjectURL(item.src);
                    return exists;
                }),
            );

            const loadableVideos = videos
                .filter((video) => itemsRef.current.every((item) => item.info.guid !== video.guid))
                .slice(0, showAllItems ? undefined : SHOW_SIZE);

            for (let i = 0; i < loadableVideos.length; i++) {
                const videoInfo = loadableVideos[i];
                const videoFile = videoInfo.data ?? (await attachmentsService.download(videoInfo.guid)).data;

                if (!videoFile) continue;

                const newItem: VideoGalleryItem = {
                    info: videoInfo,
                    src: URL.createObjectURL(videoFile),
                    poster: "",
                    isSelected: false,
                };

                setItems((currentItems) => [...currentItems, newItem]);

                // Превью-кадр получаем в фоне, чтобы плеер появился сразу
                getVideoPoster(videoFile).then((poster) => {
                    if (!poster) return;
                    setItems((currentItems) =>
                        currentItems.map((item) => (item.info.guid === videoInfo.guid ? { ...item, poster } : item)),
                    );
                });
            }
        })();
    }, [videos, showAllItems]);

    useEffect(
        () => () => {
            itemsRef.current.forEach((item) => URL.revokeObjectURL(item.src));
        },
        [],
    );

    const handleSelect = (selectedIndex: number) => {
        setItems((currentItems) =>
            currentItems.map((item, i) => (i === selectedIndex ? { ...item, isSelected: !item.isSelected } : item)),
        );
    };

    const handleRemove = async () => {
        setIsOpenConfirmDialog(false);
        setIsRemoving(true);
        await onRemoveFiles?.(selectedFiles.map((f) => f.info));
        setIsRemoving(false);
    };

    const firstItems = items.slice(0, SHOW_SIZE);
    const lastItems = items.slice(SHOW_SIZE);
    const canSelect = enableRemoveFiles && !isRemoving && !disabled;

    // На старых Android WebView inline-видео не отображается — переводим в полноэкранный режим
    const handlePlay = (event: SyntheticEvent<HTMLVideoElement>) => {
        if (!androidService.shouldPlayVideoFullscreen() || document.fullscreenElement) return;
        event.currentTarget.requestFullscreen?.().catch(() => {});
    };

    const renderItem = (item: VideoGalleryItem, index: number) => (
        <div key={item.info.guid} className="video-gallery-item my-2">
            <Row className="gx-2 align-items-center mb-1">
                {canSelect && (
                    <Col xs="auto">
                        <Input
                            type="checkbox"
                            checked={item.isSelected}
                            disabled={isRemoving}
                            onChange={() => handleSelect(index)}
                        />
                    </Col>
                )}
                <Col className="text-truncate small">{item.info.name}</Col>
            </Row>
            <video
                src={item.src}
                poster={item.poster || undefined}
                controls
                preload="metadata"
                playsInline
                onPlay={handlePlay}
                className="w-100 video-gallery-player"
            />
        </div>
    );

    return (
        <div>
            {firstItems.map((item, index) => renderItem(item, index))}
            <Collapse isOpen={showAllItems}>{lastItems.map((item, index) => renderItem(item, SHOW_SIZE + index))}</Collapse>
            {hasSelectedFiles && (
                <Row className={"mt-3"}>
                    <Col xs={12}>
                        <Button
                            color="danger"
                            disabled={disabled || isRemoving}
                            className={"w-100"}
                            onClick={() => setIsOpenConfirmDialog(true)}
                        >
                            <FontAwesomeIcon icon={faTrashAlt} /> {pluralFile(selectedFiles.length)}
                        </Button>
                    </Col>
                </Row>
            )}
            {hasLastFiles && (
                <Row xs="auto" className="gx-0 mt-2 justify-content-center align-items-center">
                    <Col>Всего {pluralFile(videos.length)}.</Col>
                    <Col>
                        <Button color="link" onClick={() => setShowAllItems(!showAllItems)}>
                            {showAllItems ? "Скрыть последние" : "Показать все"}
                        </Button>
                    </Col>
                </Row>
            )}
            <ConfirmDialog
                isOpen={isOpenConfirmDialog}
                header={"Удаление файлов"}
                confirmButtonColor={"danger"}
                onClose={() => setIsOpenConfirmDialog(false)}
                onConfirm={handleRemove}
            >
                Вы уверены, что хотите удалить выбранные файлы?
            </ConfirmDialog>
        </div>
    );
};

export default VideoGallery;
